import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, CircularProgress, Alert, Stack,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Tooltip
} from '@mui/material';
import { CheckCircle as ApproveIcon, Cancel as RejectIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { adminEndpoints } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLORS = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

export default function DetachRequestsPage() {
  const { getAccessToken } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // requestId being processed
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirm, setConfirm] = useState(null); // { requestId, status, userId }

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      const response = await fetch(adminEndpoints.detachRequests, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load detach requests');
      const data = await response.json();
      setRequests(data.requests || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleAction = async (requestId, status) => {
    setConfirm(null);
    setActionLoading(requestId);
    setError(null);
    setSuccess(null);
    try {
      const token = await getAccessToken();
      const response = await fetch(adminEndpoints.detachRequests, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requestId, status }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to update request');
      setSuccess(`Request ${status} successfully`);
      // Refresh list
      await fetchRequests();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  return (
    <Box sx={{ width: '100%', px: 3, mt: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4">Detach Requests</Typography>
          {pendingCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {pendingCount} pending request{pendingCount > 1 ? 's' : ''} awaiting approval
            </Typography>
          )}
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchRequests}
          disabled={loading}
          variant="outlined"
        >
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : requests.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No detach requests found.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Usage Plan</TableCell>
                <TableCell>API Key ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Resolved</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((req) => (
                  <TableRow key={req.requestId} hover>
                    <TableCell>
                      <Tooltip title={req.requestId}>
                        <Typography variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {req.requestId}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={req.userId}>
                        <Typography variant="body2" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {req.userId}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{req.usagePlanName || req.usagePlanId}</TableCell>
                    <TableCell>
                      <Tooltip title={req.apiKeyId}>
                        <Typography variant="body2" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {req.apiKeyId}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip label={req.status} color={STATUS_COLORS[req.status] || 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      {req.createdAt ? new Date(req.createdAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      {req.resolvedAt ? new Date(req.resolvedAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell align="center">
                      {req.status === 'pending' ? (
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Approve — will delete the API key">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={
                                  actionLoading === req.requestId
                                    ? <CircularProgress size={14} color="inherit" />
                                    : <ApproveIcon />
                                }
                                disabled={!!actionLoading}
                                onClick={() => setConfirm({ requestId: req.requestId, status: 'approved', userId: req.userId })}
                              >
                                Approve
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reject — keep the API key active">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={
                                  actionLoading === req.requestId
                                    ? <CircularProgress size={14} color="inherit" />
                                    : <RejectIcon />
                                }
                                disabled={!!actionLoading}
                                onClick={() => setConfirm({ requestId: req.requestId, status: 'rejected', userId: req.userId })}
                              >
                                Reject
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation dialog */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>
          {confirm?.status === 'approved' ? 'Approve Detach Request' : 'Reject Detach Request'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.status === 'approved'
              ? `This will permanently delete the API key for user ${confirm?.userId} and remove it from their account. This action cannot be undone.`
              : `The API key will remain active and the user's request will be rejected.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            onClick={() => handleAction(confirm.requestId, confirm.status)}
            color={confirm?.status === 'approved' ? 'success' : 'error'}
            variant="contained"
            autoFocus
          >
            Confirm {confirm?.status === 'approved' ? 'Approval' : 'Rejection'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
